import { mapLimited } from "./map-limited.js";
import { SheetData } from "./sheet-data.js";

/**
 * The keys a look-alike or vocabulary sheet chooses from, and the items it
 * fetches one at a time.
 *
 * Each sheet's page publishes its keys at `keys.json` beside it and each
 * item at `data/<key>.json` under it, written by
 * `src/assets/worksheet-records.ts`, and names the keys in `data-cb-data`.
 * A sheet fetches the keys once and then the items its words need, so no
 * request hands over the whole of the book's collocations or the sentences.
 * An item is fetched once for the page. A sheet missing any of its items
 * says it could not load, and the next draw asks again for what failed.
 */
export class SheetShards<Keys, Item> {
  readonly #fetch: typeof fetch;
  readonly #folder: string;
  readonly #keys: SheetData<Keys>;
  readonly #readItem: (body: unknown) => Item | undefined;
  readonly #limit: number;
  readonly #items = new Map<string, Promise<Item | undefined>>();

  constructor(properties: {
    readonly fetch: typeof fetch;
    /** Where the keys are published. The items are under `data/` beside it. */
    readonly address: string;
    /** The keys' shape, checked, or nothing for a body that is not it. */
    readonly readKeys: (body: unknown) => Keys | undefined;
    /** An item's shape, checked, or nothing for a body that is not one. */
    readonly readItem: (body: unknown) => Item | undefined;
    /** The most items asked for at once. */
    readonly limit: number;
  }) {
    this.#fetch = properties.fetch;
    this.#folder = properties.address.slice(
      0,
      properties.address.lastIndexOf("/") + 1,
    );
    this.#keys = new SheetData({
      fetch: properties.fetch,
      address: properties.address,
      read: properties.readKeys,
    });
    this.#readItem = properties.readItem;
    this.#limit = properties.limit;
  }

  /** The keys, or nothing where they could not be read. */
  keys(): Promise<Keys | undefined> {
    return this.#keys.load();
  }

  /** These items by key, or nothing where any of them could not be read. */
  async items(
    keys: readonly string[],
  ): Promise<ReadonlyMap<string, Item> | undefined> {
    const unique = [...new Set(keys)];
    const read = await mapLimited(unique, this.#limit, (key) =>
      this.#item(key),
    );
    const items = new Map<string, Item>();
    for (const [at, item] of read.entries()) {
      if (item === undefined) {
        return undefined;
      }
      items.set(unique[at]!, item);
    }

    return items;
  }

  async #item(key: string): Promise<Item | undefined> {
    let item = this.#items.get(key);
    if (item === undefined) {
      item = this.#request(key);
      this.#items.set(key, item);
    }
    const read = await item;
    if (read === undefined) {
      this.#items.delete(key);
    }

    return read;
  }

  async #request(key: string): Promise<Item | undefined> {
    try {
      const response = await this.#fetch(
        `${this.#folder}data/${encodeURIComponent(key)}.json`,
      );

      return response.ok ? this.#readItem(await response.json()) : undefined;
    } catch {
      return undefined;
    }
  }
}
